import type { Move, Square } from "chess.js";
import { Chess } from "chess.js";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatTime(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Returns an array of captured piece types for a given color.
 * @param moveHistory Array of chess.js Move objects
 * @param color 'w' | 'b' - the color whose pieces were captured
 * @returns Array of captured piece types (e.g., ['p', 'n', 'q'])
 */
export function getCapturedPieces(
  moveHistory: { captured?: string; color: string }[],
  color: "w" | "b"
) {
  // All possible piece types
  const pieceTypes = ["p", "n", "b", "r", "q"];
  // Count captures of the given color
  const captured: Record<string, number> = { p: 0, n: 0, b: 0, r: 0, q: 0 };
  for (const move of moveHistory) {
    if (move.captured && move.color !== color) {
      // move.color is the capturer, so the captured piece is of the opposite color
      captured[move.captured] = (captured[move.captured] || 0) + 1;
    }
  }
  // Return an array of captured piece types, e.g. ['p', 'p', 'n']
  return pieceTypes.flatMap((type) => Array(captured[type]).fill(type));
}

/**
 * Adds a piece to the given FEN string at the specified square.
 * piece: single-character FEN code (e.g., 'R', 'n', 'q', 'P', 'k', etc.)
 * Returns the new FEN string.
 */
export function addPieceToFen(
  fen: string,
  square: string,
  piece: string
): string {
  const fenParts = fen.split(" ");
  const boardRows = fenParts[0].split("/");
  const row = 8 - parseInt(square[1]);
  const col = square.charCodeAt(0) - 97;
  const rowPieces = boardRows[row].split("");
  // Expand numbers to empty squares
  let expandedRow: string[] = [];
  for (const char of rowPieces) {
    if (isNaN(parseInt(char))) {
      expandedRow.push(char);
    } else {
      expandedRow = expandedRow.concat(Array(parseInt(char)).fill("1"));
    }
  }
  // Place the piece
  expandedRow[col] = piece;
  // Compress empty squares back to numbers
  let compressedRow = "";
  let emptyCount = 0;
  for (const p of expandedRow) {
    if (p === "1") {
      emptyCount++;
    } else {
      if (emptyCount > 0) {
        compressedRow += emptyCount;
        emptyCount = 0;
      }
      compressedRow += p;
    }
  }
  if (emptyCount > 0) compressedRow += emptyCount;
  boardRows[row] = compressedRow;
  fenParts[0] = boardRows.join("/");
  return fenParts.join(" ");
}

/**
 * Gets all legal moves for a piece at the given square.
 * @param fen The current board position in FEN notation
 * @param square The square to get moves for (e.g., 'e4', 'a1')
 * @returns Array of legal destination squares (e.g., ['e5', 'e6'])
 */
export function getLegalMoves(fen: string, square: Square): Square[] {
  const chess = new Chess(fen);
  const moves = chess.moves({ square, verbose: true }) as Move[];
  return moves.map((move) => move.to);
}

export function removePieceFromFen(fen: string, square: Square): string {
  const [board, ...rest] = fen.split(" ");
  const ranks = board.split("/");
  const [file, rank] = square.split("");
  const fileIndex = "abcdefgh".indexOf(file);
  const rankIndex = 8 - parseInt(rank);

  const rankStr = ranks[rankIndex];
  let newRank = "";
  let emptyCount = 0;

  for (let i = 0; i < rankStr.length; i++) {
    const char = rankStr[i];
    if (i === fileIndex) {
      if (emptyCount > 0) {
        newRank += emptyCount.toString();
        emptyCount = 0;
      }
      newRank += "1";
    } else if (char.match(/[1-8]/)) {
      emptyCount += parseInt(char);
    } else {
      if (emptyCount > 0) {
        newRank += emptyCount.toString();
        emptyCount = 0;
      }
      newRank += char;
    }
  }

  if (emptyCount > 0) {
    newRank += emptyCount.toString();
  }

  ranks[rankIndex] = newRank;
  return [ranks.join("/"), ...rest].join(" ");
}

export function loadEmojiFont(font: string) {
  const id = `emoji-font-face-${font}`;
  if (document.getElementById(id)) return;
  let fontFace;
  switch (font) {
    case "windows":
      fontFace = new FontFace(
        "Segoe Emoji Flat",
        "url(/fonts/seguiemj-1.35-flat.ttf)"
      );
      break;
    case "twitter":
      fontFace = new FontFace("Twemoji", "url(/fonts/twemoji.ttc)");
      break;
    case "open":
      fontFace = new FontFace("OpenMoji", "url(/fonts/openmoji.ttc)");
      break;
    case "apple":
      fontFace = new FontFace(
        "Apple Color Emoji",
        "url(/fonts/AppleColorEmoji-HQ.ttc)"
      );
      break;
    case "google":
      fontFace = new FontFace(
        "Noto Color Emoji",
        "url(/fonts/NotoColorEmoji-Regular.ttf)"
      );
      break;
    default:
      return;
  }
  fontFace.load().then((loadedFace) => {
    document.fonts.add(loadedFace);
    // Mark as loaded
    const marker = document.createElement("meta");
    marker.id = id;
    document.head.appendChild(marker);
  });
}
